import { ContentLayout, PrimaryContent } from '@packages/ui/content-layout.tsx';
import { Helmet } from 'react-helmet-async';
import CompanyBasicSettings from '@client/page/management/company-settings/company-basic-settings.tsx';
import CompanyAdvancedSettings from '@client/page/management/company-settings/company-advanced-settings.tsx';
import EmailManager from '@client/page/management/company-settings/company-extra-email-setting.tsx';
import useCheckAdminRole from '@client/module/auth/check-role-hook.ts';
import { CompanyCategory } from '@client/graphql/types/graphql.ts';
import { useCompanyId } from '@packages/core/company/focused-company-context.ts';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import Card from '@packages/ui/card/card.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import { useTranslate } from '@tolgee/react';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { ApolloError } from '@apollo/client';
import { formatGraphQlError, validationErrors } from '@client/module/error/error.ts';
import { captureException } from '@sentry/browser';
import { useUpdateCompanyAll } from '@client/page/management/company-settings/logic/use-company-update-all.ts';
import ApplicationErrorView from '@client/module/error/application-error-view.tsx';
import { useCompanyService } from '@packages/core/company/company-hook.ts';

export interface CompanyForm {
  // Imprint
  imprintOwner?: string | null;
  imprintOwnerTaxId?: string | null;
  imprintCompanyName?: string | null;
  imprintCompanyTaxId?: string | null;
  imprintPhone?: string | null;
  imprintEmail?: string | null;

  // Basic
  name: string;
  logo: string | null;
  category: CompanyCategory[];

  // social
  instagramLink: string | null;
  facebookLink: string | null;
  googleLink: string | null;
  tiktokLink: string | null;

  // setting
  notificationEmails: string[];
  currency: string;
  customerDefaultLanguage: string;
}

export default function CompanySettings() {
  const { isAdmin } = useCheckAdminRole();
  const companyID = useCompanyId();
  const companyService = useCompanyService();

  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();

  const [updateCompanyAll, { error }] = useUpdateCompanyAll();

  const methods = useForm<CompanyForm>({
    defaultValues: {
      imprintOwner: '',
      imprintOwnerTaxId: '',
      imprintCompanyName: '',
      imprintCompanyTaxId: '',
      imprintPhone: '',
      imprintEmail: '',

      name: '',
      logo: '',
      category: [],

      instagramLink: '',
      facebookLink: '',
      googleLink: '',
      tiktokLink: '',

      notificationEmails: [],
      currency: '',
      customerDefaultLanguage: '',
    },
    mode: 'onChange',
    criteriaMode: 'all',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    methods.reset({
      imprintOwner: companyService.company?.imprintOwner ?? '',
      imprintOwnerTaxId: companyService.company?.imprintOwnerTaxId ?? '',
      imprintCompanyName: companyService.company?.imprintCompanyName ?? '',
      imprintCompanyTaxId: companyService.company?.imprintCompanyTaxId ?? '',
      imprintPhone: companyService.company?.imprintPhone ?? '',
      imprintEmail: companyService.company?.imprintEmail ?? '',

      name: companyService.company?.name ?? '',
      logo: companyService.company?.logo ?? '',
      category: companyService.company?.category,

      instagramLink: companyService.company?.instagramLink ?? '',
      facebookLink: companyService.company?.facebookLink ?? '',
      googleLink: companyService.company?.googleLink ?? '',
      tiktokLink: companyService.company?.tiktokLink ?? '',

      notificationEmails: companyService.company?.settings.notificationEmails ?? [],
      currency: companyService.company?.settings.currency ?? '',
      customerDefaultLanguage: companyService.company?.settings.customerDefaultLanguage ?? '',
    });
  }, [companyService, methods]);

  const onSubmit = async (updateData: CompanyForm) => {
    try {
      const result = await updateCompanyAll({
        variables: {
          companyID: companyID,
          settingID: companyService.company?.settings.id ?? '',
          input: {
            imprintOwner: updateData.imprintOwner,
            imprintOwnerTaxId: updateData.imprintOwnerTaxId,
            imprintCompanyName: updateData.imprintCompanyName,
            imprintCompanyTaxId: updateData.imprintCompanyTaxId,
            imprintPhone: updateData.imprintPhone,
            imprintEmail: updateData.imprintEmail,

            name: updateData.name,
            logo: updateData.logo,
            category: updateData.category,

            instagramLink: updateData.instagramLink,
            facebookLink: updateData.facebookLink,
            googleLink: updateData.googleLink,
            tiktokLink: updateData.tiktokLink,
          },
          settingInput: {
            notificationEmails: updateData.notificationEmails,
            currency: updateData.currency,
            customerDefaultLanguage: updateData.customerDefaultLanguage,
          },
        },
      });

      if (result.data) {
        showAlert({
          status: 'success',
          title: t('company.setting.imprint.update.alert.title', 'Update Success'),
          message: t('company.setting.imprint.update.alert.message', 'Update imprint successfully'),
        });
        location.reload();
      }
    } catch (err) {
      if (err instanceof ApolloError) {
        const applicationErrors = formatGraphQlError(err.graphQLErrors);
        const validationError = validationErrors(applicationErrors);
        for (const field in validationError) {
          methods.setError(
            field as keyof CompanyForm,
            {
              type: 'server',
              message: t(validationError[field] ?? 'validation-error.common.alert'),
            },
            { shouldFocus: true },
          );
        }
      }
      captureException(err);
    }
  };

  return (
    <ContentLayout className="relative">
      <Helmet>
        <title>Setting</title>
        <meta name="description" content="This page displays a list of company settings" />
      </Helmet>
      <PrimaryContent>
        <Card>
          <div className="sticky top-32">
            <CardHeader title={t('company-settings.header', 'Company Settings')}>
              <PrimaryButton
                onClick={() => {
                  methods.clearErrors();
                  methods.handleSubmit(onSubmit)().catch(captureException);
                }}
              >
                {t('common.button.update', 'Update')}
              </PrimaryButton>
            </CardHeader>

            <ApplicationErrorView error={error} />
          </div>
        </Card>
        <FormProvider {...methods}>
          <CompanyBasicSettings />
        </FormProvider>
        {isAdmin && (
          <FormProvider {...methods}>
            <EmailManager />
          </FormProvider>
        )}
        <FormProvider {...methods}>
          <CompanyAdvancedSettings />
        </FormProvider>
      </PrimaryContent>
    </ContentLayout>
  );
}
